import { zodResolver } from '@hookform/resolvers/zod';
import { useEffect, useMemo, useState } from 'react';
import { useForm } from 'react-hook-form';
import { z } from 'zod';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { trpc } from '~/api/trpc';
import { Loader2 } from 'lucide-react';
import { useUserStore } from '~/stores/user-store';
import BillingDraftBasicDetailsForm from './BillingDraftBasicDetailsForm';
import BillingDraftLineItemsEditor, { type DiscountDefinition } from './BillingDraftLineItemsEditor';
import { isDiscountValidForStore } from '~/lib/discount-code-store';

// Line item schema
// NOTE: GOÄ data is NOT stored on line items. GOÄ line items are derived from
// treatment variant's goaTreatmentAssociations when generating PDF invoices.
// For physical products, they are added as simple line items.
const lineItemSchema = z.object({
    id: z.string().optional(),
    description: z.string().min(1, 'Beschreibung erforderlich'),
    sequence: z.number().int(),
    servicePillarType: z.enum(['AESTHETIC', 'COSMETIC', 'HEALTH']),
    serviceMedicalType: z.enum(['MEDICAL', 'NOT_MEDICAL']),
    sourceType: z.enum(['ORIGINATED_FROM_CUSTOMER_BOOKED_CART', 'UPSELL', 'PHYSICAL_PRODUCT', 'BUNDLE_FIRST_CHARGE']),
    totalGross: z.number(),
    totalGrossDiscounted: z.number(),
    grossDiscountAmount: z.number(),
    vatRate: z.number(),
    totalNet: z.number(),
    totalNetDiscounted: z.number(),
    netDiscountAmount: z.number(),
    vatAmount: z.number(),
    vatAmountDiscounted: z.number(),
    // Either treatmentVariantId or physicalProductId must be provided
    treatmentVariantId: z.string().nullable().optional(),
    physicalProductId: z.string().nullable().optional(),
    bookableItemId: z.string().nullable().optional(),
    bookableItemAddonId: z.string().nullable().optional(),
    cartItemSourceId: z.string().nullable().optional(),
    // Bundle reference for bundle purchases
    bundleId: z.string().nullable().optional(),
    // When bundleId is set, indicates whether to consume first charge when voucher is created
    consumeFirstCharge: z.boolean().optional(),
    // Bundle voucher used to pay for this line item (existing voucher redemption at creation time)
    paidByBundleVoucherId: z.string().nullable().optional(),
    // Original price before any voucher was applied (for reverting when voucher is removed)
    // This is a client-side field only, not sent to the server
    originalPriceGross: z.number().optional(),
    // Gift card template reference for gift card purchases
    giftCardTemplateId: z.string().nullable().optional(),
    // Gift card recipient customer (optional - who the gift card is intended for)
    giftCardRecipientCustomerId: z.number().int().nullable().optional(),
    // Practitioner who documented this line item (client-side only, not sent to server)
    documentedByPractitioner: z.string().nullable().optional(),
    // Discounts applied to this line item (label is client-side only for display, not sent to server)
    discounts: z.array(z.object({
        discountCodeId: z.string().nullable().optional(),
        label: z.string(),
        discountAmountEur: z.number(),
        note: z.string().nullable().optional(),
    })).optional().default([]),
}).refine(
    (data) => data.treatmentVariantId || data.physicalProductId || data.giftCardTemplateId,
    { message: 'Entweder Behandlungsvariante, Produkt oder Wertgutschein erforderlich' }
);

// Full billing draft form schema
const billingDraftFormSchema = z.object({
    // Basic details
    customerId: z.number().int().min(1, 'Kunde erforderlich'),
    appointmentId: z.string().nullable(),
    storeId: z.number().int().min(1, 'Filiale erforderlich'),
    createdById: z.number().int().min(1, 'Ersteller erforderlich'),
    treatmentDate: z.string().min(1, 'Behandlungsdatum erforderlich'),
    billingDraftDate: z.string().min(1, 'Entwurfsdatum erforderlich'),
    dueDate: z.string().min(1, 'Fälligkeitsdatum erforderlich'),
    type: z.enum(['NORMAL', 'INFLUENCER', 'MARKETING', 'ACADEMY', 'KULANZ']),
    influencerType: z.enum(['BEAUTY_INSIDER', 'INFLUENCER', 'INFLUENCER_FLATRATE']).nullable().optional(),
    status: z.enum(['PENDING_PAYMENT', 'PAID', 'CANCELLED']),
    
    // Company details (editable)
    companyName: z.string().min(1, 'Firmenname erforderlich'),
    companyAddress: z.string().min(1, 'Adresse erforderlich'),
    companyCity: z.string().min(1, 'Stadt erforderlich'),
    companyCountry: z.string().min(1, 'Land erforderlich'),
    
    // Customer details (editable)
    customerName: z.string().min(1, 'Kundenname erforderlich'),
    customerNumber: z.string(),
    customerStreet: z.string(),
    customerZipcode: z.string(),
    customerCity: z.string(),
    customerCountry: z.string(),
    
    // Cart source
    cartSourceId: z.string().nullable().optional(),
    
    // Line items
    lineItems: z.array(lineItemSchema).min(1, 'Mindestens eine Position erforderlich'),
});

export type BillingDraftFormData = z.infer<typeof billingDraftFormSchema>;
export type BillingDraftLineItem = z.infer<typeof lineItemSchema>;

type BillingDraftCreatorProps = {
    preselectedCustomerId?: number;
    preselectedAppointmentId?: string;
    onSuccess?: (billingDraftId: string) => void;
    onCancel?: () => void;
    hideWrapper?: boolean;
    defaultTab?: 'basic' | 'lineItems';
};

const BillingDraftCreator = ({
    preselectedCustomerId,
    preselectedAppointmentId,
    onSuccess,
    onCancel,
    hideWrapper,
    defaultTab,
}: BillingDraftCreatorProps) => {
    const [activeTab, setActiveTab] = useState(defaultTab ?? 'basic');
    const trpcUtils = trpc.useUtils();
    const currentUser = useUserStore((state) => state.user);

    // Fetch all staff users for the creator selector
    const { data: usersData } = trpc.user.getAll.useQuery({ page: 0, pageSize: 100 });

    const form = useForm<BillingDraftFormData>({
        resolver: zodResolver(billingDraftFormSchema),
        defaultValues: {
            customerId: preselectedCustomerId || 0,
            appointmentId: preselectedAppointmentId || null,
            storeId: 0,
            createdById: currentUser?.id ?? 0,
            treatmentDate: new Date().toISOString().split('T')[0],
            billingDraftDate: new Date().toISOString().split('T')[0],
            dueDate: new Date(Date.now() + 14 * 24 * 60 * 60 * 1000).toISOString().split('T')[0],
            type: 'NORMAL',
            influencerType: null,
            status: 'PENDING_PAYMENT',
            companyName: '',
            companyAddress: '',
            companyCity: '',
            companyCountry: 'Deutschland',
            customerName: '',
            customerNumber: '',
            customerStreet: '',
            customerZipcode: '',
            customerCity: '',
            customerCountry: 'Deutschland',
            cartSourceId: null,
            lineItems: [],
        },
    });

    const { watch, setValue } = form;
    const selectedCustomerId = watch('customerId');
    const selectedAppointmentId = watch('appointmentId');
    const selectedStoreId = watch('storeId');
    const billingDraftType = watch('type');

    // Fetch customer info when customer is selected
    const { data: customerInfo } = trpc.billingDrafts.getCustomerAddressInfo.useQuery(
        { customerId: selectedCustomerId },
        { enabled: selectedCustomerId > 0 }
    );

    // Fetch company info when store is selected
    const { data: companyInfo } = trpc.billingDrafts.getCompanyDetailsForStore.useQuery(
        { storeId: selectedStoreId },
        { enabled: selectedStoreId > 0 }
    );

    // Fetch appointment details to get the treatment date
    const { data: appointmentData } = trpc.appointments.getOne.useQuery(
        { id: selectedAppointmentId || '' },
        { enabled: !!selectedAppointmentId }
    );

    // Fetch cart when appointment is selected
    const { data: cartData } = trpc.carts.getByAppointment.useQuery(
        { appointmentId: selectedAppointmentId || '' },
        { enabled: !!selectedAppointmentId }
    );

    // Fetch documented attributions (cart items marked as performed + variant-only documented services).
    // Drives which cart items are populated and adds documented services that are not part of the cart.
    const { data: documentedAttr } = trpc.medicalRecord.getDocumentedAttributionsForAppointment.useQuery(
        { appointmentId: selectedAppointmentId || '' },
        { enabled: !!selectedAppointmentId }
    );

    // Fetch available bundle vouchers for the customer (for auto-applying to cart items)
    const { data: availableVouchers } = trpc.billingDrafts.getAvailableVouchersForCustomer.useQuery(
        { customerId: selectedCustomerId },
        { enabled: selectedCustomerId > 0 }
    );

    // Fetch valid discount codes for the treatment date
    const treatmentDate = watch('treatmentDate');
    const { data: validDiscountCodes } = trpc.billingDrafts.getValidDiscountCodes.useQuery(
        { treatmentDate },
        { enabled: !!treatmentDate }
    );

    // Fetch customer type (new vs returning) for discount code eligibility checks
    const { data: customerTypeData } = trpc.billingDrafts.getCustomerType.useQuery(
        { customerId: selectedCustomerId },
        { enabled: selectedCustomerId > 0 }
    );

    // Fetch influencer bundle price overrides (only when type is INFLUENCER)
    const isInfluencer = billingDraftType === 'INFLUENCER';
    const { data: influencerPriceOverrides } = trpc.billingDrafts.getInfluencerPriceOverrides.useQuery(
        { storeId: selectedStoreId },
        { enabled: isInfluencer && selectedStoreId > 0 }
    );

    // Auto-fill treatment date, store, type, and influencerType from appointment
    useEffect(() => {
        if (appointmentData && appointmentData.steps?.[0]?.dateFrom) {
            const appointmentDate = new Date(appointmentData.steps[0].dateFrom);
            const dateString = appointmentDate.toISOString().split('T')[0];
            setValue('treatmentDate', dateString);
        }
        if (appointmentData?.storeId) {
            setValue('storeId', appointmentData.storeId);
        }
        if ((appointmentData as any)?.type) {
            const appointmentType = (appointmentData as any).type as 'NORMAL' | 'INFLUENCER' | 'MARKETING' | 'ACADEMY' | 'KULANZ';
            setValue('type', appointmentType);
            if (appointmentType === 'INFLUENCER') {
                setValue('influencerType', (appointmentData?.influencerType ?? null) as any);
            } else {
                setValue('influencerType', null);
            }
        }
    }, [appointmentData, setValue]);

    // Auto-fill customer info
    useEffect(() => {
        if (customerInfo) {
            setValue('customerName', customerInfo.customerName);
            setValue('customerNumber', customerInfo.customerNumber);
            setValue('customerStreet', customerInfo.customerStreet);
            setValue('customerZipcode', customerInfo.customerZipcode);
            setValue('customerCity', customerInfo.customerCity);
            setValue('customerCountry', customerInfo.customerCountry);
        }
    }, [customerInfo, setValue]);

    // Auto-fill company info
    useEffect(() => {
        if (companyInfo) {
            setValue('companyName', companyInfo.companyName);
            setValue('companyAddress', companyInfo.companyAddress);
            setValue('companyCity', companyInfo.companyCity);
            setValue('companyCountry', companyInfo.companyCountry);
        }
    }, [companyInfo, setValue]);

    // Auto-fill line items from cart
    // NOTE: GOÄ data is NOT stored on line items. GOÄ line items are derived
    // from treatment variant's goaTreatmentAssociations when generating PDF invoices.
    useEffect(() => {
        if (!cartData) {
            return;
        }
        // Wait until the documented attributions have loaded for the selected appointment, so the
        // combined line items (cart + variant-only) are complete before the editor applies discounts once.
        if (selectedAppointmentId && documentedAttr === undefined) {
            return;
        }

        setValue('cartSourceId', cartData.id);

        const isBundlesDisabled = billingDraftType === 'INFLUENCER'
            || billingDraftType === 'MARKETING'
            || billingDraftType === 'ACADEMY'
            || billingDraftType === 'KULANZ';

        // Build a map of treatmentVariantId -> available vouchers for quick lookup
        // Track claimed units to avoid double-assigning the same voucher
        const vouchersByVariant = new Map<string, Array<typeof availableVouchers extends (infer T)[] | undefined ? T : never>>();
        const claimedUnits = new Map<string, number>();

        if (availableVouchers) {
            for (const voucher of availableVouchers) {
                const existing = vouchersByVariant.get(voucher.treatmentVariantId) || [];
                existing.push(voucher);
                vouchersByVariant.set(voucher.treatmentVariantId, existing);
            }
        }

        // When a treatment record exists, use documentation to drive line item population.
        // - If attributions exist: only keep documented cart items + add variant/product line items.
        // - If no attributions (Keine Behandlung): populate nothing.
        // If no treatment record exists at all, fall back to the full cart (unfiltered).
        const hasTreatmentRecord = documentedAttr?.hasTreatmentRecord ?? false;
        const hasAnyAttribution = documentedAttr?.hasAnyAttribution ?? false;
        const documentedCartItems = documentedAttr?.documentedCartItems ?? [];
        const documentedCartItemMap = new Map(
            documentedCartItems.map((d) => [d.cartItemId, d])
        );
        const cartItemsToPopulate = hasTreatmentRecord
            ? (hasAnyAttribution
                ? cartData.items.filter((item: any) => documentedCartItemMap.has(item.id))
                : [])
            : cartData.items;

        const cartLineItems: BillingDraftLineItem[] = cartItemsToPopulate.map((item: any) => {
            const variant = item.treatmentVariant;
            const treatment = variant.treatment;

            // Check if documentation overrides this cart item with a bundle
            const docInfo = documentedCartItemMap.get(item.id);
            const docBundleOverride = docInfo?.bundleId ? docInfo : null;
            const practitionerName = docInfo?.practitionerName ?? null;

            // Base GROSS price (B2C). Prefer the frozen cart snapshot so the line item
            // reflects the price the item was booked at; fall back to the live variant
            // price only for legacy carts that predate the snapshot.
            // Exception: redemptions store the bundle unit price as totalGross (for
            // bookedPrice math), but the billing draft base should be the full variant
            // price so removing the voucher reverts to the correct "no bundle" price.
            const storedGross = item.totalGross != null && !item.isBundleRedemption && !(isBundlesDisabled && item.bundleId)
                ? Number(item.totalGross)
                : null;
            const priceCents = variant.priceCents;
            const originalGross = storedGross ?? priceCents / 100;
            // VAT is always 19% for B2C invoices - reverse calculate from gross
            const vatRate = 0.19;

            // For bundles being purchased, use the bundle's unit price instead of the variant price.
            // Redemption items keep bundleId set but should flow through the regular-service branch
            // so the customer's existing bundle voucher is auto-matched for payment.
            // Also handle documentation bundle override (practitioner selected a bundle for this cart item)
            const isBundle = !isBundlesDisabled && ((!!item.bundleId && !item.isBundleRedemption) || !!docBundleOverride);
            let finalTotalGross = originalGross;
            let selectedVoucherId: string | null = null;
            let storedOriginalPrice: number | undefined = undefined;
            let effectiveBundleId: string | null = null;
            let effectiveBundleName: string | null = null;
            let effectiveBundleQuantity: number | null = null;

            if (docBundleOverride && !isBundlesDisabled) {
                // Documentation overrides with a bundle
                effectiveBundleId = docBundleOverride.bundleId!;
                effectiveBundleName = docBundleOverride.bundleName ?? null;
                if (docBundleOverride.bundleUnitPriceGross != null && docBundleOverride.bundleUnitQuantity != null) {
                    finalTotalGross = docBundleOverride.bundleUnitPriceGross * docBundleOverride.bundleUnitQuantity;
                    effectiveBundleQuantity = docBundleOverride.bundleUnitQuantity;
                }
            } else if (isBundle && item.bundle?.unitPriceGross) {
                effectiveBundleId = item.bundleId;
                effectiveBundleName = item.bundle?.name ?? null;
                effectiveBundleQuantity = item.bundle?.unitQuantity ?? null;
                // Prefer the stored bundle snapshot; fall back to live bundle math for legacy carts.
                finalTotalGross = storedGross ?? Number(item.bundle.unitPriceGross) * item.bundle.unitQuantity;
            } else if (!isBundle) {
                // Check if customer has an available bundle voucher for this treatment variant
                const matchingVouchers = vouchersByVariant.get(item.treatmentVariantId) || [];
                for (const voucher of matchingVouchers) {
                    const alreadyClaimed = claimedUnits.get(voucher.id) || 0;
                    const effectiveRemaining = voucher.remainingUnits - alreadyClaimed;
                    if (effectiveRemaining >= 1) {
                        selectedVoucherId = voucher.id;
                        finalTotalGross = voucher.unitPriceGross;
                        storedOriginalPrice = originalGross;
                        claimedUnits.set(voucher.id, alreadyClaimed + 1);
                        break;
                    }
                }
            }

            // Influencer override takes priority: use bundle unit price if available
            if (isInfluencer && !isBundle && influencerPriceOverrides?.[item.treatmentVariantId] !== undefined) {
                storedOriginalPrice = originalGross;
                finalTotalGross = influencerPriceOverrides[item.treatmentVariantId];
                selectedVoucherId = null;
            }

            // Line items are set at BASE prices (no discounts applied).
            // Discounts are handled by the editor via initialDiscountDefinitions + recalculateAllDiscounts.
            const finalTotalNet = finalTotalGross / (1 + vatRate);
            const finalVatAmount = finalTotalGross - finalTotalNet;

            return {
                description: isBundle && effectiveBundleName
                    ? `${effectiveBundleName} (${effectiveBundleQuantity ?? 1}x)`
                    : variant.name,
                sequence: 0,
                servicePillarType: treatment.servicePillarType as 'AESTHETIC' | 'COSMETIC' | 'HEALTH',
                serviceMedicalType: treatment.serviceMedicalType as 'MEDICAL' | 'NOT_MEDICAL',
                sourceType: 'ORIGINATED_FROM_CUSTOMER_BOOKED_CART' as const,
                totalGross: finalTotalGross,
                totalGrossDiscounted: finalTotalGross,
                grossDiscountAmount: 0,
                vatRate,
                totalNet: finalTotalNet,
                totalNetDiscounted: finalTotalNet,
                netDiscountAmount: 0,
                vatAmount: finalVatAmount,
                vatAmountDiscounted: finalVatAmount,
                treatmentVariantId: item.treatmentVariantId,
                physicalProductId: null,
                bookableItemId: item.bookableItemId || null,
                bookableItemAddonId: item.bookableItemAddonId || null,
                cartItemSourceId: item.id,
                bundleId: isBundle ? (effectiveBundleId || item.bundleId || null) : null,
                // Default to consuming first charge for bundles
                consumeFirstCharge: isBundle ? true : undefined,
                paidByBundleVoucherId: selectedVoucherId,
                originalPriceGross: storedOriginalPrice,
                documentedByPractitioner: practitionerName,
                discounts: [],
            };
        });

        // Documented services that are not part of the cart (variant-only attributions).
        // Populated at the variant's base price; discounts are applied afterward by the editor
        // via targetAll + validTreatmentVariantIds matching, just like cart line items.
        const vatRate = 0.19;
        const variantLineItems: BillingDraftLineItem[] = (documentedAttr?.variantOnlyAttributions ?? []).map((v) => {
            const hasBundle = !isBundlesDisabled && !!v.bundleId && v.bundleUnitPriceGross != null;
            const baseGross = hasBundle
                ? v.bundleUnitPriceGross! * (v.bundleUnitQuantity ?? 1)
                : v.priceCents / 100;

            let finalTotalGross = baseGross;
            let selectedVoucherId: string | null = null;
            let storedOriginalPrice: number | undefined = undefined;

            if (!hasBundle) {
                const matchingVouchers = vouchersByVariant.get(v.treatmentVariantId) || [];
                for (const voucher of matchingVouchers) {
                    const alreadyClaimed = claimedUnits.get(voucher.id) || 0;
                    const effectiveRemaining = voucher.remainingUnits - alreadyClaimed;
                    if (effectiveRemaining >= 1) {
                        selectedVoucherId = voucher.id;
                        finalTotalGross = voucher.unitPriceGross;
                        storedOriginalPrice = baseGross;
                        claimedUnits.set(voucher.id, alreadyClaimed + 1);
                        break;
                    }
                }
            }

            // Influencer override for variant-only documented services
            if (isInfluencer && !hasBundle && influencerPriceOverrides?.[v.treatmentVariantId] !== undefined) {
                storedOriginalPrice = baseGross;
                finalTotalGross = influencerPriceOverrides[v.treatmentVariantId];
                selectedVoucherId = null;
            }

            const totalNet = finalTotalGross / (1 + vatRate);
            const vatAmount = finalTotalGross - totalNet;
            return {
                description: hasBundle && v.bundleName
                    ? `${v.bundleName} (${v.bundleUnitQuantity ?? 1}x)`
                    : v.name,
                sequence: 0,
                servicePillarType: v.servicePillarType as 'AESTHETIC' | 'COSMETIC' | 'HEALTH',
                serviceMedicalType: v.serviceMedicalType as 'MEDICAL' | 'NOT_MEDICAL',
                sourceType: 'UPSELL' as const,
                totalGross: finalTotalGross,
                totalGrossDiscounted: finalTotalGross,
                grossDiscountAmount: 0,
                vatRate,
                totalNet,
                totalNetDiscounted: totalNet,
                netDiscountAmount: 0,
                vatAmount,
                vatAmountDiscounted: vatAmount,
                treatmentVariantId: v.treatmentVariantId,
                physicalProductId: null,
                bookableItemId: null,
                bookableItemAddonId: null,
                cartItemSourceId: null,
                bundleId: v.bundleId ?? null,
                consumeFirstCharge: hasBundle ? true : undefined,
                paidByBundleVoucherId: selectedVoucherId,
                originalPriceGross: storedOriginalPrice,
                documentedByPractitioner: v.practitionerName ?? null,
                discounts: [],
            };
        });

        // Documented products that are not part of the cart
        const productLineItems: BillingDraftLineItem[] = (documentedAttr?.productAttributions ?? []).map((p) => {
            const totalGross = p.unitPriceGross;
            const totalNet = totalGross / (1 + vatRate);
            const vatAmount = totalGross - totalNet;
            return {
                description: p.name,
                sequence: 0,
                servicePillarType: 'AESTHETIC' as const,
                serviceMedicalType: 'NOT_MEDICAL' as const,
                sourceType: 'PHYSICAL_PRODUCT' as const,
                totalGross,
                totalGrossDiscounted: totalGross,
                grossDiscountAmount: 0,
                vatRate,
                totalNet,
                totalNetDiscounted: totalNet,
                netDiscountAmount: 0,
                vatAmount,
                vatAmountDiscounted: vatAmount,
                treatmentVariantId: null,
                physicalProductId: p.physicalProductId,
                bookableItemId: null,
                bookableItemAddonId: null,
                cartItemSourceId: null,
                bundleId: null,
                consumeFirstCharge: undefined,
                paidByBundleVoucherId: null,
                originalPriceGross: undefined,
                documentedByPractitioner: p.practitionerName ?? null,
                discounts: [],
            };
        });

        const combined = [...cartLineItems, ...variantLineItems, ...productLineItems].map((li, index) => ({
            ...li,
            sequence: index + 1,
        }));

        setValue('lineItems', combined);
    }, [cartData, availableVouchers, documentedAttr, selectedAppointmentId, setValue, billingDraftType, influencerPriceOverrides]);

    // Build initial discount definitions from cart discounts (for the editor to apply via recalculation)
    const initialDiscountDefinitions = useMemo((): DiscountDefinition[] => {
        if (!cartData || cartData.items.length === 0) { return []; }

        const definitionMap = new Map<string, DiscountDefinition>();
        for (const item of (cartData as any).items) {
            if (!item.discounts || item.discounts.length === 0) { continue; }
            for (const d of item.discounts) {
                if (!d.type || d.discountValue == null || !d.discountCodeId) { continue; }
                if (definitionMap.has(d.discountCodeId)) { continue; }

                // Look up code restrictions from valid discount codes if available
                const codeInfo = (validDiscountCodes ?? []).find((c: any) => c.id === d.discountCodeId);

                // Skip prepopulated cart discounts whose code is scoped to stores
                // that do not include the billing draft's store.
                if (codeInfo && !isDiscountValidForStore(codeInfo.validStoreIds ?? [], selectedStoreId > 0 ? selectedStoreId : null)) {
                    continue;
                }

                definitionMap.set(d.discountCodeId, {
                    id: `cart-${d.discountCodeId}`,
                    source: 'cart',
                    discountCodeId: d.discountCodeId,
                    label: d.discountCodeName || 'Rabatt',
                    type: codeInfo
                        ? (codeInfo.type === 'PERCENTAGE_DISCOUNT' ? 'PERCENTAGE' : 'ABSOLUTE')
                        : 'ABSOLUTE',
                    value: codeInfo
                        ? codeInfo.discountValue
                        : Number(d.discountValue),
                    targetAll: true,
                    targetIndices: [],
                    validBookableItemIds: codeInfo?.validBookableItemIds ?? [],
                    validTreatmentVariantIds: codeInfo?.validTreatmentVariantIds ?? [],
                    validBundleIds: codeInfo?.validBundleIds ?? [],
                    validProductIds: codeInfo?.validProductIds ?? [],
                });
            }
        }

        return Array.from(definitionMap.values());
    }, [cartData, validDiscountCodes, selectedStoreId]);

    // Create billing draft mutation
    const createBillingDraftMutation = trpc.billingDrafts.create.useMutation({
        onSuccess: (data) => {
            toast.success('Auftrag wurde erstellt');
            trpcUtils.billingDrafts.getAll.invalidate();
            onSuccess?.(data.id);
        },
        onError: (error) => {
            toast.error(`Fehler: ${error.message}`);
        },
    });

    const handleSubmit = form.handleSubmit((data) => {
        createBillingDraftMutation.mutate(data);
    });

    const validateBasicFields = async () => {
        const results = await Promise.all([
            form.trigger('customerId'),
            form.trigger('storeId'),
        ]);
        return results.every(Boolean);
    };

    const handleGoToLineItems = async () => {
        const isValid = await validateBasicFields();
        if (isValid) {
            setActiveTab('lineItems');
        }
    };

    const handleTabChange = async (tab: string) => {
        if (tab === 'lineItems') {
            const isValid = await validateBasicFields();
            if (!isValid) return;
        }
        setActiveTab(tab);
    };

    const lineItems = watch('lineItems');
    const totals = lineItems.reduce(
        (acc, item) => ({
            totalGross: acc.totalGross + item.totalGross,
            totalGrossDiscounted: acc.totalGrossDiscounted + item.totalGrossDiscounted,
            totalNet: acc.totalNet + item.totalNet,
            totalNetDiscounted: acc.totalNetDiscounted + item.totalNetDiscounted,
            vatAmount: acc.vatAmount + item.vatAmount,
            vatAmountDiscounted: acc.vatAmountDiscounted + item.vatAmountDiscounted,
        }),
        { totalGross: 0, totalGrossDiscounted: 0, totalNet: 0, totalNetDiscounted: 0, vatAmount: 0, vatAmountDiscounted: 0 }
    );

    const content = (
        <>
            <div className='flex items-center gap-6 mb-4'>
                <div className='flex items-center gap-3'>
                    <Label className='text-sm text-muted-foreground whitespace-nowrap'>Erstellt von:</Label>
                    <Select
                        value={String(watch('createdById'))}
                        onValueChange={(v) => setValue('createdById', Number(v))}
                    >
                        <SelectTrigger className='w-64 h-8'>
                            <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                            {usersData?.data.map((user) => (
                                <SelectItem key={user.id} value={String(user.id)}>
                                    {user.firstName} {user.lastName}
                                </SelectItem>
                            ))}
                        </SelectContent>
                    </Select>
                </div>
                <div className='flex items-center gap-3'>
                    <Label className='text-sm text-muted-foreground whitespace-nowrap'>Typ:</Label>
                    <Select
                        value={watch('type')}
                        onValueChange={(v) => {
                            setValue('type', v as 'NORMAL' | 'INFLUENCER' | 'MARKETING' | 'ACADEMY' | 'KULANZ');
                            setValue('lineItems', []);
                            if (v !== 'INFLUENCER') {
                                setValue('influencerType', null);
                            }
                        }}
                    >
                        <SelectTrigger className='w-48 h-8'>
                            <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                            <SelectItem value='NORMAL'>Standard</SelectItem>
                            <SelectItem value='INFLUENCER'>Influencer</SelectItem>
                            <SelectItem value='MARKETING'>Marketing</SelectItem>
                            <SelectItem value='ACADEMY'>Academy</SelectItem>
                            <SelectItem value='KULANZ'>Kulanz/Beschwerde</SelectItem>
                        </SelectContent>
                    </Select>
                </div>
                {watch('type') === 'INFLUENCER' && (
                    <div className='flex items-center gap-3'>
                        <Label className='text-sm text-muted-foreground whitespace-nowrap'>Influencer-Typ:</Label>
                        <Select
                            value={watch('influencerType') || ''}
                            onValueChange={(v) => setValue('influencerType', v as any)}
                        >
                            <SelectTrigger className='w-64 h-8'>
                                <SelectValue placeholder='Typ auswählen...' />
                            </SelectTrigger>
                            <SelectContent>
                                <SelectItem value='BEAUTY_INSIDER'>Beauty Insider</SelectItem>
                                <SelectItem value='INFLUENCER'>Influencer Normal</SelectItem>
                                <SelectItem value='INFLUENCER_FLATRATE'>Influencer Pauschalversteuerung</SelectItem>
                            </SelectContent>
                        </Select>
                    </div>
                )}
            </div>
            <Tabs value={activeTab} onValueChange={handleTabChange}>
                <TabsList className='grid w-full grid-cols-2'>
                    <TabsTrigger value='basic'>Grunddaten</TabsTrigger>
                    <TabsTrigger value='lineItems'>
                        Positionen ({lineItems.length})
                    </TabsTrigger>
                </TabsList>

                <TabsContent value='basic' className='mt-6'>
                    <BillingDraftBasicDetailsForm
                        form={form}
                        preselectedCustomerId={preselectedCustomerId}
                        preselectedAppointmentId={preselectedAppointmentId}
                    />
                </TabsContent>

                <TabsContent value='lineItems' className='mt-6'>
                    <BillingDraftLineItemsEditor
                        form={form}
                        totals={totals}
                        validDiscountCodes={validDiscountCodes ?? []}
                        initialDiscountDefinitions={initialDiscountDefinitions}
                        appointmentCouponCodes={appointmentData?.couponCodes}
                        appointmentStartTime={appointmentData?.steps?.[0]?.dateFrom ?? null}
                        customerType={customerTypeData?.customerType ?? null}
                        noTreatmentPerformed={(documentedAttr?.hasTreatmentRecord ?? false) && !(documentedAttr?.hasAnyAttribution ?? false)}
                        noTreatmentPractitioners={documentedAttr?.treatmentPractitionerNames}
                    />
                </TabsContent>
            </Tabs>
            <div className='flex justify-between mt-4'>
                <Button variant='outline' onClick={onCancel}>
                    Abbrechen
                </Button>
                <div className='flex gap-2'>
                    {activeTab === 'basic' && (
                        <Button onClick={handleGoToLineItems}>
                            Weiter zu Positionen
                        </Button>
                    )}
                    {activeTab === 'lineItems' && (
                        <>
                            <Button variant='outline' onClick={() => setActiveTab('basic')}>
                                Zurück
                            </Button>
                            <Button
                                onClick={handleSubmit}
                                disabled={createBillingDraftMutation.isPending || lineItems.length === 0 || selectedCustomerId === 0}
                            >
                                {createBillingDraftMutation.isPending && (
                                    <Loader2 className='mr-2 h-4 w-4 animate-spin' />
                                )}
                                Auftrag erstellen
                            </Button>
                        </>
                    )}
                </div>
            </div>
        </>
    );

    if (hideWrapper) {
        return content;
    }

    return (
        <Card className='w-full'>
            <CardHeader>
                <CardTitle>Auftrag erstellen</CardTitle>
            </CardHeader>
            <CardContent>
                {content}
            </CardContent>
        </Card>
    );
};

export default BillingDraftCreator;
